import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Briefcase, Loader2, RefreshCcw, AlertCircle, Users, Trash2 } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface SharedTeamPosition {
  id: number
  uuid?: string
  share_id?: number
  share_uuid?: string
  title: string
  department: string
  status: string
  candidates_count: number
  created_date?: string
  shared_by?: string | null
  shared_at?: string | null
}

interface TeamSharedPositionsProps {
  teamId: string
}

export default function TeamSharedPositions({ teamId }: TeamSharedPositionsProps) {
  const [positions, setPositions] = useState<SharedTeamPosition[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  useEffect(() => {
    if (!teamId) return
    fetchSharedPositions()
  }, [teamId])

  const fetchSharedPositions = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await apiClient.getTeamSharedPositions(teamId)
      if (response.success && response.data) {
        const rawPositions = Array.isArray(response.data)
          ? response.data
          : response.data.positions || []

        const normalized = rawPositions.map((position: any) => ({
          id: position.id,
          uuid: position.uuid,
          share_id: position.share_id,
          share_uuid: position.share_uuid,
          title: position.title,
          department: position.department || 'General',
          status: position.status || 'open',
          candidates_count: position.candidates_count || position.applicants_count || 0,
          created_date: position.created_date || position.created_at,
          shared_by: position.shared_by || position.sharedBy || null,
          shared_at: position.shared_at || position.sharedAt || null
        }))

        setPositions(normalized)
      } else if (response.error || response.message) {
        setError(response.error || response.message || 'Unable to load shared positions for this team')
        setPositions([])
      } else {
        setPositions([])
      }
    } catch (err) {
      console.error('Failed to load shared positions', err)
      setError(err instanceof Error ? err.message : 'Unable to load shared positions for this team')
      setPositions([])
    } finally {
      setLoading(false)
    }
  }

  const revokeShare = async (shareUuid?: string, fallbackId?: string | number, positionTitle?: string) => {
    const identifier = shareUuid || (typeof fallbackId === 'string' ? fallbackId : undefined)

    if (!identifier) {
      alert('Missing share identifier; cannot revoke this share.')
      return
    }

    if (!window.confirm(`Stop sharing "${positionTitle || 'this position'}" with this team?`)) return
    try {
      setRevokingId(String(identifier))
      const response = await apiClient.removePositionShare(identifier)
      if (response.success) {
        setPositions(prev => prev.filter(p => String(p.share_uuid || '') !== String(identifier)))
      } else {
        alert(response.error || 'Failed to revoke position share')
      }
    } catch (err) {
      console.error('Failed to revoke share', err)
      alert(err instanceof Error ? err.message : 'Failed to revoke position share')
    } finally {
      setRevokingId(null)
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <span>Loading shared positions...</span>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-red-600 text-center space-y-2">
          <AlertCircle className="w-8 h-8" />
          <p className="font-medium">{error}</p>
          <button
            onClick={fetchSharedPositions}
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Retry
          </button>
        </div>
      )
    }

    if (positions.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center text-gray-600 space-y-2">
          <Briefcase className="w-12 h-12 text-gray-400" />
          <p className="text-lg font-semibold text-gray-900">No positions shared</p>
          <p className="text-sm">Positions shared with this team will appear here.</p>
        </div>
      )
    }

    return (
      <div className="space-y-3">
        {positions.map((position) => (
          <motion.div
            key={position.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <div className="flex-1 flex items-center space-x-3">
              <Briefcase className="w-5 h-5 text-gray-400" />
              <div className="cursor-pointer" onClick={() => window.location.href = `/recruiter/positions/${position.uuid || position.id}`}>
                <p className="font-medium text-gray-900">{position.title}</p>
                <div className="flex flex-wrap items-center text-sm text-gray-500 space-x-3">
                  <span>{position.department}</span>
                  {position.shared_by && (
                    <span className="flex items-center space-x-1">
                      <Users className="w-3 h-3" />
                      <span>Shared by {position.shared_by}</span>
                    </span>
                  )}
                  {position.shared_at && (
                    <span>
                      Shared {new Date(position.shared_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <span className="text-sm text-gray-600">{position.candidates_count} candidates</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                position.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {position.status}
              </span>
              <button
                onClick={() => revokeShare(position.share_uuid, position.id, position.title)}
                disabled={revokingId === String(position.share_uuid || '')}
                className={`p-2 rounded text-red-600 hover:bg-red-50 ${revokingId === String(position.share_uuid || '') ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Stop sharing position"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Positions Shared with Team ({positions.length})
        </h3>
        <button
          onClick={fetchSharedPositions}
          disabled={loading}
          className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {renderContent()}
    </div>
  )
}
